import { get } from 'lodash';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { useTranslation, Trans } from 'react-i18next';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Link, Redirect, Switch, useHistory, useLocation, withRouter } from "react-router-dom";
import { clearSimulatedData, setLoadingSimulatedUser, setSimulatedData, setSimulatedUser } from '../../actions/config.actions';
import Analytics from '../../influencer/pages/Analytics/Analytics';
import ErrorPage from '../../influencer/pages/ErrorPage/ErrorPage';
import Home from "../../influencer/pages/home/Home";
import LearningCenter from '../../influencer/pages/LearningCenter/LearningCenter';
import Offer from '../../influencer/pages/offer/Offer';
import Profile from "../../influencer/pages/profile/Profile";
import Settings from '../../influencer/pages/settings/Settings';
import SignUp from '../../influencer/pages/signup/SignUp';
import axios from '../../utils/axiosConfig';
import useIsMounted from '../hooks/useIsMounted';
import CookiesInfluencer from '../pages/CookiesInfluencer/CookiesInfluencer';
import PrivacyPolicy from '../pages/PrivacyPolicy/PrivacyPolicy';
import StyleGuide from "../pages/StyleGuide/StyleGuide";
import TOS from '../pages/TOS/TOS';
import Welcome from '../pages/Welcome/Welcome';
import Loader from './Loader/Loader';
import PrivateRoute from './PrivateRoute';
import SafeRoute from './SafeRoute';
import ScrollToTop from './ScrollToTop';
import './UserSimulation/UserSimulation.scss';
import useBreakpoint from '../hooks/useBreakpoint';
import { getCookie } from '../../utils/cookies';
import CompleteRegistration from '../pages/CompleteRegistration/CompleteRegistration'

const RouterMap = ({ setSimulatedUser }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const { user } = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const breakpoint = useBreakpoint();

  const [state, setState] = useState({
    simulatedUserId: null,
    simulatedUserEmail: ''
  });

  useEffect(() => {
    if (user && user.force_password_change && !location.pathname.startsWith('/team/')) {
      history.replace('/team/set-password');
    }
  }, [user, location.pathname]);

  useEffect(() => {
    // This routes already handles redirects
    if (['/404', '/signin', '/team/', /sign-up/]
      .some(path => location.pathname.match(path))
    ) return;

    if (user) {
      if (user.activated) { return; }

      // If user hasn't complete sign up redirect to it
      const hasSocialProfile = get(user, 'social.inst.nickname') ||
        get(user, 'social.tikt.nickname') ||
        get(user, 'social.yout.nickname') ||
        get(user, 'social.link.nickname');

      if (
        !location.pathname.includes('/sign-up/step-2') &&
        user.social && !hasSocialProfile
      ) {
        return history.replace({ pathname: '/sign-up/step-2' });
      }

      if (
        !location.pathname.includes('/sign-up/step-3') &&
        !(
          get(user, 'city_id') &&
          get(user, 'dob') &&
          get(user, 'gender')
        )
      ) {
        history.replace({ pathname: '/sign-up/step-3' });
      }
    }
  }, [location.pathname]);

  const isMounted = useIsMounted();
  const queryParams = new URLSearchParams(location.search);
  const querySimulatedUserId = queryParams.get('simulated_user_id');
  const localStorageSimulatedUserId = localStorage.getItem('simulatedUser');
  const userId = querySimulatedUserId || localStorageSimulatedUserId;
  const language = getCookie('i18next') || getCookie('locale') || 'en';

  const refreshHome = useCallback(() => {
    if (queryParams.has('simulated_user_id')) {
      queryParams.delete('simulated_user_id');
      history.replace({
        search: queryParams.toString(),
      });
    }
    dispatch(clearSimulatedData());
    setSimulatedUser(null);
    setState({
      ...state,
      simulatedUserId: null
    });

    if (window.location.href.match(/\/influencers\/404/) && location.pathname.match(/^\/offer\//)) {
      history.push(location.pathname);
    }

  }, []);

  if (querySimulatedUserId && querySimulatedUserId != localStorageSimulatedUserId) {
    dispatch(clearSimulatedData());
  }

  useEffect(() => {
    if (isMounted && userId) {
      setSimulatedUser(userId);
      dispatch(setLoadingSimulatedUser(true));

      axios.get(`/api/v1/influencers/users/${userId}`)
        .then(({ data }) => {
          if (data) {
            setState({
              simulatedUserId: userId,
              simulatedUserEmail: data.email
            });
            dispatch(setSimulatedData(data));
          }
          dispatch(setLoadingSimulatedUser(false));
          return null;
        })
        .catch((e) => {
          console.error(e);
          dispatch(setLoadingSimulatedUser(false));
        });
    } else {
      setSimulatedUser(null);
      setState({
        simulatedUserId: null,
        simulatedUserEmail: null
      });
    }
  }, [location.search]);

  const influencerBannerAlert = (url, bannerBody) => (
    <Alert
      variant="warning"
      className='m-4 d-block'
      style={
        {
          top: location.pathname.includes('/offer/') ? 50 : 0
        }
      }>
      <Trans
        i18nKey={bannerBody}
        components={[
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          />
        ]}
      />
    </Alert>
  );

  const influencerCollectiveBanner = () => {
    if(user.beauty_collective_member || user.fragrance_collective_member)
      return;

    if(user.gender == "Male")
      return <Alert variant="warning" className='m-4 d-block' style={{
        top: location.pathname.includes('/offer/') ? 50 : 0
      }}>
        Join Obviously’s <Link to="/offer/16645">Fashion & Fragrance Crew</Link>: Score free products and early access to exclusive drops!
        
      </Alert>

    return <Alert variant="warning" className='m-4 d-block' style={{
        top: location.pathname.includes('/offer/') ? 50 : 0
      }}>
        Join the Obviously <Link to="/offer/12647">Beauty Collective</Link>: Get priority access to gifted beauty products, fragrances, & exclusive campaigns!
      </Alert>
  }

  const facebookFastTrackBanner = () => (
    <Alert variant="warning" className='m-4 d-block' style={{
      top: location.pathname.includes('/offer/') ? 50 : 0
    }}>
      Start Earning! You Qualify for the Facebook{' '}
      <a
        href="https://www.facebook.com/creator_programs/signup?referral_code=obviously"
        target="_blank"
        rel="noopener noreferrer"
      >
        Fast Track Creator Program
      </a>
    </Alert>
  );

  const influencerBanner = () => {
    if(user.restricted_admin)
      return;

    if(user.facebook_fast_track_banner_enabled)
      return facebookFastTrackBanner();

    if(user.influencer_collective_banner_enabled)
      return influencerCollectiveBanner();

    return;
  }

  return (
    <Fragment>
      <ScrollToTop />
      {(state.simulatedUserId || localStorageSimulatedUserId) && isMounted &&
        <div className='close-user-simulation alert alert-warning' onClick={() => refreshHome()}>
          {t('influencers.admin.simulated_user', { email: state.simulatedUserEmail })}
        </div>
      }
      { /* enable the following to show a banner to the clients and influencers */}
      {
        user && influencerBanner()
      }
      <Switch>
        <Redirect exact from="/home" to='/' />
        <PrivateRoute path="/profile/*" component={Profile} />
        <PrivateRoute exact path="/profile" component={Profile} />
        <PrivateRoute path="/styles" component={StyleGuide} />
        <PrivateRoute path="/analytics">
          <Analytics />
        </PrivateRoute>
        <PrivateRoute path="/settings" component={Settings} />
        <SafeRoute path="/privacy-policy" component={PrivacyPolicy} />
        <SafeRoute path="/cookies-influencer" component={CookiesInfluencer} />
        <SafeRoute path="/terms-of-service" component={TOS} language={language} />
        <SafeRoute path="/offer/:offerId/:activeTab?">
          {querySimulatedUserId && (localStorageSimulatedUserId === null || localStorageSimulatedUserId != querySimulatedUserId) ?
            <Loader loading={true} error={null} />
            :
            <Offer simulatedUserId={state.simulatedUserId} language={language} />
          }
        </SafeRoute>
        <SafeRoute path="/sign-up/:step" component={SignUp} />
        <SafeRoute exact path="/sign-up" component={SignUp} />
        <SafeRoute path={["/signin", "/forgot-your-password", "/reset-your-password/:resetPasswordToken", "/team/signin", "/team/set-password", "/team/set-profile", "/team/forgot-your-password", "/team/reset-your-password/:resetPasswordToken"]} component={Welcome} />
        <SafeRoute
          path={
            [
              "/signin", "/forgot-your-password", "/reset-your-password/:resetPasswordToken",
              "/team/signin", "/team/set-password", "/team/forgot-your-password",
              "/team/reset-your-password/:resetPasswordToken",
            ]
          }
          component={Welcome}
        />
        <SafeRoute path="/welcome/:completeRegistrationToken" component={CompleteRegistration} />
        <SafeRoute path="/404" component={ErrorPage} />
        <PrivateRoute path="/learning-center" component={LearningCenter} language={language} />
        <PrivateRoute exact path="/" component={Home} simulatedUserId={state.simulatedUserId} />
      </Switch>
    </Fragment>
  );
};

const mapDispatchToProps = (dispatch) => ({
  setSimulatedUser: (userId) => dispatch(setSimulatedUser(userId))
});

export default withRouter(connect(null, mapDispatchToProps)(RouterMap));

